/**
 * Notification center helpers shared by the bell, the notifications page and push-tap handling
 * (which runs outside React). Type names match the backend catalog
 * (DispatchRealtime/Notifications/NotificationTypes.cs) and double as the push event_name.
 */
import { hasNavigationHistory, navigateApp } from './navigation';

/** Mirrors NotificationService.RetentionDays on the backend (older entries are hidden). */
export const NOTIFICATION_RETENTION_DAYS = 30;

export const NOTIFICATION_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  JobPaid: { label: 'Invoice paid', description: 'A customer paid an invoice through the online payment page' },
  JobPendingApproval: { label: 'Job waiting for approval', description: 'A technician closed a job and it needs approval' },
  TechnicianAssigned: { label: 'Job assigned', description: 'A technician was assigned to a job (sent to that technician)' },
  NewMessage: { label: 'New chat message', description: 'The office sent a technician a chat message in the team chat (sent to that technician)' },
  NewSms: { label: 'New text on a job', description: "A customer texted about a technician's job (sent to that technician)" },
  EstimatePaid: { label: 'Estimate paid', description: 'A customer paid an estimate through the online payment page' },
  EstimateSigned: { label: 'Estimate signed', description: 'A customer signed an estimate through the online estimate page' },
  JobCompleted: { label: 'Job completed', description: 'A non-admin completed a job, or approved one waiting for approval' },
  JobCanceled: { label: 'Job canceled', description: 'A non-admin canceled a job' },
  JobDeleted: { label: 'Job deleted', description: 'A non-admin deleted a job' },
  JobConvertedToEstimate: { label: 'Job converted to estimate', description: 'A non-admin converted a job to an estimate' },
  TechnicianCheckedIn: { label: 'Technician checked in', description: 'A technician checked in to a job' },
  TechnicianCheckedOut: { label: 'Technician checked out', description: 'A technician checked out of a job' },
  MissedCall: { label: 'Missed call', description: 'An incoming call was not answered by anyone' },
  InboundSms: { label: 'New text message', description: 'A customer sent a text message to one of your numbers' },
  TechnicianMessage: { label: 'Message from a technician', description: 'A technician sent the office a chat message in the team chat' },
};

/** Leave the notifications page for the previous screen, or jobs on a cold launch with nothing to go back to. */
export const leaveNotificationsPage = (): void => {
  if (hasNavigationHistory()) window.history.back();
  else navigateApp('/jobs', 'replace');
};

/** Fired after something outside React (a push tap) changed read state. */
export const NOTIFICATIONS_CHANGED_EVENT = 'notifications-changed';

const OPEN_JOB_EVENT = 'open-job-request';
const OPEN_ESTIMATE_EVENT = 'open-estimate-request';
const pending: Record<string, string | null> = { [OPEN_JOB_EVENT]: null, [OPEN_ESTIMATE_EVENT]: null };

/**
 * Ask the app to open an entity over the current page. Kept as a pending value so a push tap on a
 * cold start — before Dashboard has mounted — still opens it (Dashboard drains it on mount).
 */
function requestOpen(event: string, id: string): void {
  pending[event] = id;
  window.dispatchEvent(new Event(event));
}

/** Open whatever is pending now and whenever one is requested. Returns the unsubscribe. */
function subscribeOpenRequests(event: string, open: (id: string) => void): () => void {
  const drain = () => {
    const id = pending[event];
    pending[event] = null;
    if (id) open(id);
  };
  drain();
  window.addEventListener(event, drain);
  return () => window.removeEventListener(event, drain);
}

export const requestOpenJob = (jobId: string) => requestOpen(OPEN_JOB_EVENT, jobId);
export const requestOpenEstimate = (estimateId: string) => requestOpen(OPEN_ESTIMATE_EVENT, estimateId);
/** Dashboard: open requested jobs. */
export const subscribeOpenJobRequests = (open: (jobId: string) => void) => subscribeOpenRequests(OPEN_JOB_EVENT, open);
/** Dashboard: open requested estimates. */
export const subscribeOpenEstimateRequests = (open: (estimateId: string) => void) =>
  subscribeOpenRequests(OPEN_ESTIMATE_EVENT, open);

export function openNotificationTarget(type: string, payload: Record<string, unknown> | undefined): void {
  const str = (key: string) => (typeof payload?.[key] === 'string' ? (payload[key] as string) : null);

  switch (type) {
    case 'NewSms':
    case 'InboundSms': {
      const conversationId = str('conversation_id');
      navigateApp(conversationId ? `/messaging?conversation_id=${encodeURIComponent(conversationId)}` : '/messaging');
      return;
    }
    case 'NewMessage':
    case 'TechnicianMessage':
      navigateApp('/messaging');
      return;
    case 'EstimatePaid':
    case 'EstimateSigned':
    case 'JobConvertedToEstimate': {
      const estimateId = str('estimate_id');
      if (estimateId) requestOpenEstimate(estimateId);
      return;
    }
    case 'MissedCall':
      navigateApp('/voip');
      return;
    case 'JobDeleted':
      return; // nothing left to open
    default: {
      // TechnicianAssigned, JobPaid, JobPendingApproval, JobCompleted, JobCanceled, TechnicianChecked* — and any future job-scoped type.
      const jobId = str('job_id');
      if (jobId) requestOpenJob(jobId);
    }
  }
}
